import { db } from './db.js';

const apiBase = (import.meta.env.VITE_ODOO_API_URL || '').replace(/\/$/, '');
const apiToken = import.meta.env.VITE_ODOO_API_TOKEN || '';
const storeNames = ['zones', 'employes', 'equipes', 'categories', 'unites', 'variantes', 'produits', 'produits_variantes', 'stock_lignes', 'parametres'];

let activeSync = null;

export function isSyncConfigured() {
  return Boolean(apiBase && apiToken);
}

export async function pendingCount() {
  return db.mouvements.where('statut').equals('en_attente').count();
}

// Mouvements définitivement rejetés par le serveur (ex. zone archivée) : ne
// sont plus jamais renvoyés automatiquement, sinon ils bloqueraient les autres
// mouvements du même lot indéfiniment — bug réel corrigé côté serveur et ici.
export async function errorCount() {
  return db.mouvements.where('statut').equals('erreur').count();
}

export async function syncNow(prints = []) {
  if (activeSync) return activeSync;
  activeSync = performSync(prints).finally(() => { activeSync = null; });
  return activeSync;
}

// `prints` : impressions déclenchées par le bouton « Imprimer » de l'App
// Employé (voir App.svelte) — envoyées avec le même appel que la sync des
// mouvements pour ne pas multiplier les allers-retours réseau. Pas de retry
// automatique en arrière-plan pour celles-ci (contrairement aux mouvements) :
// c'est un clic utilisateur explicite, jamais rejoué tout seul.
async function performSync(prints = []) {
  if (!isSyncConfigured()) throw new Error('Synchronisation non configurée');
  const pending = await db.mouvements.where('statut').equals('en_attente').limit(100).toArray();
  const lastSync = await db.meta.get('last_sync_at');
  const response = await fetch(`${apiBase}/soontech_terrain/api/sync`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'call',
      id: Date.now(),
      params: {
        mouvements: pending.map(({ local_id, statut, created_at, ...movement }) => movement),
        last_sync_at: lastSync?.value || null,
        prints,
      },
    }),
  });
  if (!response.ok) throw new Error(`Serveur indisponible (${response.status})`);
  const envelope = await response.json();
  if (envelope.error) throw new Error(envelope.error.data?.message || envelope.error.message || 'Synchronisation refusée');
  const result = envelope.result;

  await db.transaction('rw', [...storeNames.map((name) => db[name]), db.mouvements, db.meta], async () => {
    for (const name of storeNames) {
      const rows = result.cache?.[name] || [];
      if (rows.length) await db[name].bulkPut(rows);
    }
    const accepted = new Map((result.accepted || []).map((item) => [item.uuid_client, item]));
    for (const movement of pending) {
      const server = accepted.get(movement.uuid_client);
      if (!server) continue; // pas encore traité par le serveur, on le renverra au prochain essai
      if (server.status === 'rejected') {
        // Rejeté définitivement (ex. zone archivée) : on arrête de le renvoyer,
        // sinon il bloquerait à nouveau tous les autres mouvements du lot.
        await db.mouvements.update(movement.local_id, { statut: 'erreur', erreur_message: server.message || 'Rejeté par le serveur.' });
      } else {
        await db.mouvements.update(movement.local_id, { statut: 'synchronise', server_id: server.server_id });
      }
    }
    await db.meta.put({ key: 'last_sync_at', value: result.synchronized_at });
  });
  if (prints.length) {
    const rejected = (result.prints_accepted || []).filter((item) => item.status === 'rejected');
    if (rejected.length) throw new Error(rejected.map((item) => item.message).join(' '));
  }
  return result;
}

export function startAutomaticSync(onChange) {
  const run = async () => {
    if (!navigator.onLine || !isSyncConfigured()) return;
    try { await syncNow(); onChange?.(null); } catch (error) { onChange?.(error); }
  };
  const interval = setInterval(run, 10 * 60 * 1000);
  window.addEventListener('online', run);
  run();
  return () => { clearInterval(interval); window.removeEventListener('online', run); };
}
